import { ApolloClient } from 'apollo-client';
import { InMemoryCache } from 'apollo-cache-inmemory';
import { WebSocketLink } from 'apollo-link-ws';
import { split, ApolloLink } from 'apollo-link';
import { onError } from 'apollo-link-error';
import { Platform } from 'react-native';
import { HttpLink } from 'apollo-link-http';
import { getMainDefinition } from 'apollo-utilities';

import bugsnagClient from './bugsnag-client';
import NetworkError from './NetworkError';
import WebsocketError from './WebsocketError';
import subscriptionClient from './subscription-client';
import { LYSTER_API_ENDPOINT } from '../env';


class ApolloError extends Error {
  constructor(...args) {
    super(...args);
    const graphQLErrors = args[0];

    Error.captureStackTrace && Error.captureStackTrace(this, ApolloError);

    this.message = graphQLErrors[0].message;
    this.name = 'ApolloError';
  }
}


// Instantiate required constructor fields
const cache = new InMemoryCache();


const wsLink = new WebSocketLink(subscriptionClient);

const httpLink = new HttpLink({
  uri: `${LYSTER_API_ENDPOINT}graphql`,
});

subscriptionClient.onConnected((evt) => {
  bugsnagClient.leaveBreadcrumb('Websocket Connected', {
    ...evt,
    type: 'request',
  });
});

subscriptionClient.onDisconnected((evt) => {
  if (Platform.OS === 'android') {
    subscriptionClient.close();
  }

  bugsnagClient.leaveBreadcrumb('Websocket Disconnected', {
    ...evt,
    type: 'request',
  });
});

subscriptionClient.onReconnected((evt) => {
  bugsnagClient.leaveBreadcrumb('Websocket Reconnected', {
    ...evt,
    type: 'request',
  });
});

subscriptionClient.onConnecting((evt) => {
  bugsnagClient.leaveBreadcrumb('Websocket Connecting..', {
    ...evt,
    type: 'request',
  });
});

subscriptionClient.onReconnecting((evt) => {
  bugsnagClient.leaveBreadcrumb('Websocket Reconnecting..', {
    ...evt,
    type: 'request',
  });
});

subscriptionClient.onError(({ message, target, ...rest }) => {
  const { url } = target;
  bugsnagClient.notify(new WebsocketError(message || 'Websocket Error'), {
    url,
    type: 'request',
    severity: 'info',
    ...rest,
  });
});


const loggerLink = new ApolloLink((operation, forward) => {
  // bugsnagClient.leaveBreadcrumb(`GraphQL Request: ${operation.operationName}`, { type: 'request' });

  operation.setContext({ start: new Date(), operation: operation.operationName });
  return forward(operation).map((response) => {
    const responseTime = new Date() - operation.getContext().start;
    const operationName = operation.getContext().operation;
    bugsnagClient.leaveBreadcrumb(`GraphQL started`, {
      type: 'request', operationName, responseTime,
    });
    return response;
  });
});

const errorLink = onError(({ graphQLErrors, networkError, operation }) => {
  const responseTime = new Date() - operation.getContext().start,
        operationName = operation.getContext().operation;

  if (graphQLErrors && graphQLErrors.length > 0) {
    graphQLErrors.forEach((error) => bugsnagClient.leaveBreadcrumb(`Error: ${error.message}`, {
      type: 'request', operationName, responseTime, message: error.message,
    }));
    bugsnagClient.notify(new ApolloError(graphQLErrors, operation));
  } else if (networkError) {
    bugsnagClient.leaveBreadcrumb(`GraphQL failed`, {
      type: 'request', operationName, responseTime,
    });
    bugsnagClient.notify(new NetworkError(networkError.message));
  }
});

const transportLink = split(
  // split based on operation type
  ({ query }) => {
    const definition = getMainDefinition(query);
    return (
      definition.kind === 'OperationDefinition'
      && definition.operation === 'subscription'
    );
  },
  wsLink,
  httpLink,
);


const link = ApolloLink.from([
  loggerLink,
  errorLink,
  transportLink,
]);

const client = new ApolloClient({
  // Provide required constructor fields
  cache,
  link,

  // Provide some optional constructor fields
  // name: 'react-web-client',
  // version: '1.3',
  // queryDeduplication: false,
  defaultOptions: {
    watchQuery: {
      fetchPolicy: 'cache-and-network',
    },
  },
});

export default client;
